import { useTranslations } from "@/contexts/Translation";
import { Answer, Response as ResponseType } from "../../types";
import { H2, H3 } from "../Headers/Headings";
import EmScore from "./EmScore";
import { ResponderNotes } from "./ResponderNotes";
import { useSurvey } from "@/contexts/Survey";
import { getScoreDescription } from "@/lib/getScoreDescription";
import Recommendation from "../Recommendations/Recommendation";
import Card from "../Card/Card";
import styles from "./Response.module.css";

type ResponseProps = ResponseType & {
  candidate: string;
};

export const Response = ({ surveyId, status, candidate, responder, recommendation, recommendationName, recommendationTitle, message, answers, notes }: ResponseProps) => {
  const t = useTranslations("questions");
  // Notes are only sent to the owner and the person the survey is on behalf of
  const { isOwner, canEdit } = useSurvey();
  const responderName = responder.endsWith("anonymous.com") ? t("anonymousName") : responder;
  const Answer = ({ question, value }: Answer) => {
    return (
      <>
        <div className={styles.answerLabel}>
          <H3 variant='flush'>{t(question, candidate)}</H3>
          {value && value > 0 ? <p className={styles.answerDescription}>{getScoreDescription(t, value)}</p> : null}
        </div>
        <div>
          <EmScore size={3} score={value} />
        </div>
      </>
    );
  };

  return (
    <div>
      <H2>{t("responseHeading", responderName)}</H2>
      {status !== "responded" && <p className={styles.nonResponseText}>{t("hasNotResponded", responder)}</p>}
      {recommendation && (
        <>
          <H3 variant='section'>{t("recommendationHeading")}</H3>
          <Recommendation recommendation={{ surveyId, recommendation, recommendationName, recommendationTitle }} />
        </>
      )}

      {message && (
        <>
          <H3 variant='section'>{t("messageHeading")}</H3>
          <Card className={styles.card} variant='fixed'>
            <p className={styles.messageText}>{message}</p>
          </Card>
        </>
      )}
      {answers.length > 0 && (
        <>
          <H3 variant='section'>{t("answersHeading")}</H3>
          <Card className={styles.card} variant='fixed'>
            <div className={styles.answersGrid}>
              {answers.map((a) => (
                <Answer key={a.question} {...a} />
              ))}
            </div>
          </Card>
          {(isOwner || canEdit) && (
            <Card className={styles.card} variant='fixed'>
              <ResponderNotes notes={notes} responder={responder} />
            </Card>
          )}
        </>
      )}
    </div>
  );
};
